/**
 * Invitation API Client - PROJ-032 User Invitation System
 *
 * Handles user invitation endpoints for onboarding flow.
 */

import { fetchWithAuth, fetchPublic } from './http-client';

// Types
export interface CreateInvitationRequest {
  expiresInHours?: number;
  personalMessage?: string;
  sendEmail?: boolean;
}

export interface CreateInvitationResponse {
  invitationToken: string;
  invitationUrl: string;
  expiresAt: string;
  userId: string;
  userEmail: string;
  fullName: string;
  emailSent: boolean;
}

export interface ValidateInvitationResponse {
  valid: boolean;
  userId: string;
  userEmail: string;
  fullName: string;
  username?: string;
  expiresAt: string;
  hoursRemaining: number;
}

export interface CompleteInvitationRequest {
  token: string;
  authMethod: 'local' | 'google' | 'microsoft';
  password?: string;
}

export interface CompleteInvitationResponse {
  success: boolean;
  userId: string;
  email: string;
  fullName: string;
  message: string;
}

export interface InvitationStatusResponse {
  hasActiveInvitation: boolean;
  expiresAt?: string;
  hoursRemaining?: number;
  isExpired?: boolean;
  createdAt?: string;
}

// API Base URL
const API_BASE = '/api/v1';

/**
 * Create an invitation for a user
 * Requires authentication (admin only)
 */
export async function createInvitation(
  userId: string,
  options: CreateInvitationRequest = {}
): Promise<CreateInvitationResponse> {
  const response = await fetchWithAuth(`${API_BASE}/users/${userId}/invite`, {
    method: 'POST',
    body: JSON.stringify(options),
    context: 'Crear invitación de usuario',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || errorData.message || `Error ${response.status}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Resend invitation email for a user
 * Requires authentication (admin only)
 */
export async function resendInvitation(
  userId: string,
  options: CreateInvitationRequest = {}
): Promise<CreateInvitationResponse> {
  const response = await fetchWithAuth(`${API_BASE}/users/${userId}/resend-invitation`, {
    method: 'POST',
    body: JSON.stringify(options),
    context: 'Reenviar invitación',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || errorData.message || `Error ${response.status}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get invitation status for a user
 * Requires authentication (admin only)
 */
export async function getInvitationStatus(userId: string): Promise<InvitationStatusResponse | null> {
  const response = await fetchWithAuth(`${API_BASE}/users/${userId}/invitation-status`, {
    method: 'GET',
    context: 'Obtener estado de invitación',
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || errorData.message || `Error ${response.status}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.data;
}

export interface ResendInvitationPublicResponse {
  success: boolean;
  message: string;
}

/**
 * Resend invitation email (PUBLIC - no auth required)
 * Used on the accept-invite page when token has expired.
 * Always returns success (anti-enumeration).
 */
export async function resendInvitationPublic(email: string): Promise<ResendInvitationPublicResponse> {
  const response = await fetchPublic(`${API_BASE}/auth/invitations/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `Error ${response.status}`);
  }

  const data = await response.json();
  return data.data;
}

/**
 * Validate an invitation token (PUBLIC - no auth required)
 * Used on the accept-invite page before user sets password
 */
export async function validateInvitation(token: string): Promise<ValidateInvitationResponse> {
  const response = await fetchPublic(`${API_BASE}/auth/invitations/validate?token=${encodeURIComponent(token)}`, {
    method: 'GET',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorCode = errorData.error?.code || 'UNKNOWN_ERROR';
    const errorMessage = errorData.error?.message || errorData.message || `Error ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).code = errorCode;
    (error as any).details = errorData.error?.details;
    throw error;
  }

  const data = await response.json();
  return data.data;
}

/**
 * Complete an invitation by setting password or linking CIAM (PUBLIC - no auth required)
 * Used on the accept-invite page to activate the account
 */
export async function completeInvitation(
  request: CompleteInvitationRequest
): Promise<CompleteInvitationResponse> {
  const response = await fetchPublic(`${API_BASE}/auth/invitations/complete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorCode = errorData.error?.code || 'UNKNOWN_ERROR';
    const errorMessage = errorData.error?.message || errorData.message || `Error ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).code = errorCode;
    (error as any).details = errorData.error?.details;
    throw error;
  }

  const data = await response.json();
  return data.data;
}
